<template>
	<Transition
		@enter="onEnter"
		@leave="onLeave"
	>
		<div
			v-if="data.visibleMobileMenu"
			id="mobile-primary-menu"
			ref="nav"
			class="fixed inset-0 z-[80] bg-background-secondary w-full md:max-w-md top-[3.75rem] xl:top-[6.875rem] overflow-hidden px-4 pb-3 flex flex-col"
		>
			<div class="flex items-center gap-2 mb-2 mt-7">
				<div class="flex items-center gap-1.5">
					<NuxtIcon
						name="map-pin"
						class="text-base text-icon-primary"
						filled
						aria-hidden="true"
					/>
					<h3 class="font-secondary text-neutral-900 text-sm relative">
						<template v-if="state.visibleSelector">
							Naše pobočky
						</template>
						<template v-else-if="selectedHypermarket">
							{{ selectedHypermarketInstance?.content?.name }}
						</template>
						<template v-else>
							Vyberte vaši pobočku
						</template>
					</h3>
				</div>

				<div class="ml-auto flex items-center gap-4">
					<button
						v-if="!selectedHypermarket && !state.visibleSelector"
						type="button"
						class="btn btn-primary btn-xs justify-center text-center max-xs:grow"
						@click="handleOpenSelector('Zvolit pobočku')"
					>
						Zvolit pobočku
					</button>
					<template v-if="selectedHypermarket && state.visibleMenu">
						<button type="button" class="text-xs text-body-link underline" @click="handleOpenSelector('Změnit')">
							Změnit
						</button>
						<button type="button" class="btn btn-primary btn-xs justify-center text-center max-xs:grow" @click="handleOpenDetail">
							Info
						</button>
					</template>

					<button
						v-if="state.visibleDetail || state.visibleSelector"
						class="text-icon-primary hover:bg-button-secondary-surface-hover rounded-full transition ease min-h-[1.875rem]"
						type="button"
						aria-label="Zavřít"
						@click="showOnlySection('visibleMenu')"
					>
						<NuxtIcon name="x" filled class="text-2xl" />
					</button>
				</div>
			</div>

			<div v-if="state.visibleMenu" class="flex-1 overflow-y-auto relative">
				<HeaderAsideNavTabDetailMenu :menu-links="menuLinks" :menu-secondary-links="menuSecondaryLinks" @update:click="toggleMobileMenu" />
			</div>

			<div v-if="state.visibleDetail" class="relative flex-1 overflow-y-auto border-t border-border-secondary rounded-b-lg">
				<HeaderAsideNavTabDetailHypermarket class="border-none" @update:click="toggleMobileMenu" />
			</div>

			<div v-if="state.visibleSelector" class="relative border-t border-border-secondary flex-1 rounded-b-lg min-h-0 max-h-full">
				<!-- <HypermarketSelectionBanner
					class="px-4"
					show-links
					hide-category-filter
					list-with-search
				>
					<template #header>
						<h3 class="typo-h4 mb-1.5 text-heading-primary">
							Přejít na stránky jiné pobočky
						</h3>
					</template>
				</HypermarketSelectionBanner> -->
				<PartialsHypermarketSelectorList
					v-model:selected="selectedHypermarket"
					:options="selectorOptions"
					list-type
					class="!overflow-auto"
					@update:selected="showOnlySection('visibleMenu')"
				/>
			</div>
		</div>
	</Transition>
	<ClientTeleport>
		<Transition name="fade">
			<LazyUiBackdrop v-model:visible="data.visibleMobileMenu" />
		</Transition>
	</ClientTeleport>
</template>

<script setup lang="ts">
	import {storeToRefs} from 'pinia';
	import {gsap} from 'gsap';
	import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';
	import type {Header, MenuLink} from '~/types/header';
	import type {Option} from '~/types/formkitSelectbox';

	const props = defineProps<{
		model: Header
		menuLinks: MenuLink[]
		menuSecondaryLinks: MenuLink[]
	}>();

	const data = useVModel(props, 'model');

	const state = reactive({
		visibleMenu: true,
		visibleDetail: false,
		visibleSelector: false,
	});

	function showOnlySection(section: keyof typeof state) {
		Object.keys(state).forEach((key) => {
			state[key as keyof typeof state] = false;
		});

		state[section] = true;
	}

	const hypermarketsStore = useStoryblokHypermarketsStore();
	const {selectedHypermarket, selectedHypermarketInstance, groupedBranches} = storeToRefs(hypermarketsStore);
	const {requirement: branchSelectorRequirement} = useBranchSelectorAvailabilityRequirement();
	const availabilityBranchSlugs = computed(() => branchSelectorRequirement.value
		? (groupedBranches.value.hypermarkets ?? [])
			.concat(groupedBranches.value.fresh ?? [])
			.map((branch) => branch.slug)
		: [],
	);
	const {data: branchesPageAvailability} = useBranchesPageAvailability(availabilityBranchSlugs);

	const selectorOptions = computed<Option[]>(() => {
		const requirement = branchSelectorRequirement.value;
		const branches = (groupedBranches.value.hypermarkets ?? []).concat(groupedBranches.value.fresh ?? []);

		return branches.map((branch) => {
			const disabled = requirement
				? !branchesPageAvailability.value[branch.slug]?.[requirement.key]
				: false;

			return {
				label: branch.content.name,
				value: branch.slug,
				disabled,
				disabledText: disabled ? requirement?.disabledText : undefined,
			};
		});
	});

	const nav = ref(null);
	const cta = ref(null);

	const onEnter = (el: Element, onComplete: () => void) => {
		const tl = gsap.timeline({onComplete});
		tl
			.from(el, {opacity: 0})
			.from(nav.value, {xPercent: -100}, '<')
			.from(cta.value, {opacity: 0});
	};

	const onLeave = (el: Element, onComplete: () => void) => {
		const tl = gsap.timeline({onComplete});
		tl.to(el, {opacity: 0});
	};

	function toggleMobileMenu() {
		data.value.visibleMobileMenu = !data.value.visibleMobileMenu;
	}

	function handleOpenSelector(text: string) {
		showOnlySection('visibleSelector');
		trackHeaderEvent('hm_selector_leftside', text);
	}

	function handleOpenDetail() {
		showOnlySection('visibleDetail');
		trackHeaderEvent('hm_selector_leftside', 'Info');
	}
</script>
